import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { RootLayoutContent } from '@/app/layouts/RootLayout';
import { NavigationConfig } from '@/app/types/navigation';
import { PAGE_SIZES } from '@/common/components/ui/pagination-controls';
import { usePagesNavigation } from '@/common/hooks/usePagesNavigation';
import { PaginationParams, PolicyFilters } from '@/policy/api/FetchPolicyList';
import { StartPolicyErrorDialog } from '@/policy/component/Dialogs/StartPolicyErrorDialog';
import { PolicyList } from '@/policy/component/PolicyList/PolicyList';
import { useFetchPaginatedPoliciesSummary } from '@/policy/hooks/useFetchPaginatedPolicyList';
import { PolicySortField } from '@/policy/types/SortTypes';

export default function PolicyListPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const showErrorDialog = Boolean(location.state?.showStartPolicyError);
  const submittedPolicyNumber = location.state?.submittedPolicyNumber as
    | string
    | undefined;
  const toastShown = useRef(false);

  useEffect(() => {
    if (submittedPolicyNumber && !toastShown.current) {
      toastShown.current = true;
      toast.success(
        t('statusBar.submitSuccess', { policyNumber: submittedPolicyNumber }),
      );
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [submittedPolicyNumber, t, navigate, location.pathname]);

  const closeErrorDialog = () => {
    navigate(location.pathname, { replace: true, state: {} });
  };

  const handleRefreshErrorDialog = () => {
    closeErrorDialog();
    refetch();
  };

  const [pagination, setPagination] = useState<PaginationParams>({
    pageIndex: 0,
    pageSize: PAGE_SIZES[2],
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [filters, setFilters] = useState<PolicyFilters>({
    accountName: '',
    fileName: '',
    policyNumber: '',
    insurer: '',
    technicalSupport: '',
    status: '',
  });

  const {
    paginatedPolicies,
    isLoading: isPoliciesLoading,
    refetch,
    lastRefreshedAt,
  } = useFetchPaginatedPoliciesSummary(pagination, filters);

  const handlePageChange = (newPageIndex: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: 0, pageSize: newPageSize }));
  };

  const handleSort = (field: PolicySortField) => {
    setPagination((prev) => {
      const isSortingBySameField = prev.sortBy === field;

      if (!isSortingBySameField) {
        return {
          ...prev,
          sortBy: field,
          sortOrder: 'asc',
        };
      }

      return {
        ...prev,
        sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
      };
    });
  };

  const handleFilterChange = (field: string, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleRefresh = () => {
    refetch();
  };
  const { pages, currentPage } = usePagesNavigation();

  const navigationConfig: NavigationConfig = {
    title: t('policyList'),
    backAction: { hasBackAction: false },
    pageNavigation: {
      currentPage,
      pages,
    },
  };

  return (
    <RootLayoutContent navigationConfig={navigationConfig}>
      <div className="flex flex-col h-full w-full">
        <PolicyList
          policies={paginatedPolicies.items}
          isLoading={isPoliciesLoading}
          totalPages={paginatedPolicies.totalPages}
          totalPolicies={paginatedPolicies.totalItems}
          currentPage={pagination.pageIndex}
          pageSize={pagination.pageSize}
          sortBy={pagination.sortBy}
          sortOrder={pagination.sortOrder}
          filters={filters}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          lastRefreshedAt={lastRefreshedAt}
        />
      </div>

      <StartPolicyErrorDialog
        open={showErrorDialog}
        onClose={handleRefreshErrorDialog}
      />
    </RootLayoutContent>
  );
}
